import { Subscription } from 'rxjs';
import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import { DeliveryReportPdfService } from '../../../shared/pdf-tools/pdf-report/delivery-report-pdf.service';
import { ToastsService } from '../../../shared/toast/toast.service';
import { IpoTranslatePipe } from '@ip-only/front-end-library';
import { isPdfDownloadActivationKey } from './popup-pdf.utils';

export interface PopupPdfPayload {
  ifsNumber: string;
  deliveryOrderId: string;
  locale: string;
  address: string;
  rolloutPointType?: RolloutProgressReportEntities.v3.MarkerTypes;
}

export interface PopupPdfDownloadContext {
  getPayload: (context?: unknown) => PopupPdfPayload | null;
  isDestroyed: () => boolean;
  markForCheck?: () => void;
}

export function createPopupPdfContext(
  getPayload: PopupPdfDownloadContext['getPayload'],
  isDestroyed: () => boolean,
  markForCheck?: () => void,
): PopupPdfDownloadContext {
  return {
    getPayload,
    isDestroyed,
    markForCheck,
  };
}

export class PopupPdfDownloadController {
  isPdfDownloadInProgress = false;
  private activePdfSubscription: Subscription | null = null;

  constructor(
    private readonly pdfService: DeliveryReportPdfService,
    private readonly toastsService: ToastsService,
    private readonly translate: IpoTranslatePipe,
    private readonly ctx: PopupPdfDownloadContext,
  ) {}

  onDownloadPreviewPdf(event: MouseEvent, context?: unknown): void {
    event.stopPropagation();
    if (this.isPdfDownloadInProgress) return;
    const payload = this.ctx.getPayload(context);
    if (!payload || !payload.ifsNumber || !payload.deliveryOrderId) return;
    const { subscription } = this.pdfService.downloadPdf({
      ...payload,
      onMutate: () => {
        this.isPdfDownloadInProgress = true;
        this.ctx.markForCheck?.();
      },
      onSettled: () => {
        if (this.ctx.isDestroyed()) return;
        this.activePdfSubscription = null;
        this.isPdfDownloadInProgress = false;
        this.ctx.markForCheck?.();
      },
    });
    this.activePdfSubscription = subscription;
  }

  onDownloadPreviewPdfKeyDown(event: KeyboardEvent, context?: unknown): void {
    if (!isPdfDownloadActivationKey(event)) return;
    event.preventDefault();
    this.onDownloadPreviewPdf(event as unknown as MouseEvent, context);
  }

  cleanup(): void {
    this.activePdfSubscription?.unsubscribe();
    this.activePdfSubscription = null;
    if (this.isPdfDownloadInProgress) {
      this.toastsService.addToast(
        {
          type: 'error',
          message: this.translate.transform(
            'ordersOverview.downloadPdfCancelledPopup',
          ),
        },
        5000,
      );
    }
    this.isPdfDownloadInProgress = false;
  }
}
